import { InfoCircleFilled } from "@ant-design/icons";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Grid,
  InputLabel,
  FormControlLabel,
  Switch,
  Divider,
  Button,
  CircularProgress,
  Tooltip,
  TextField,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import { useQueryClient } from "@tanstack/react-query";
import { ApiResponse } from "common/models/ApiResponse";
import Loader from "components/Loader";
import MainCard from "components/MainCard";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import { showServerErrors } from "utils/formUtils";
import { showError, showSuccess } from "utils/showMessages";
import { GetReminderSettings } from "../api/hooks";
import { UpdateReminderSettings } from "../api/mutation";
import { ReminderSchema, IReminderSettings } from "../api/types";

const ReminderSettingsTab = () => {
  const { libraryID } = useParams();
  const editMode = libraryID !== undefined;
  const navigateTo = useNavigate();
  const queryClient = useQueryClient();

  const updateMutation = UpdateReminderSettings();
  const {
    handleSubmit,
    setError,
    register,
    reset,
    formState: { isSubmitting, errors },
  } = useForm<IReminderSettings>({
    resolver: zodResolver(ReminderSchema),
  });

  const [overDueReminderChecked, setOverDueReminderChecked] =
    useState<boolean>(false);
  const handleOverDueReminderChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setOverDueReminderChecked(event.target.checked);
  };

  const [dueDateReminderChecked, setDueDateReminderChecked] =
    useState<boolean>(false);
  const handleDueDateReminderChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setDueDateReminderChecked(event.target.checked);
  };

  const getReminderSettingsQuery = GetReminderSettings(libraryID || "");
  useEffect(() => {
    if (getReminderSettingsQuery?.data) {
      reset(getReminderSettingsQuery.data.apiData);
      setOverDueReminderChecked(
        getReminderSettingsQuery.data.apiData.isOverDueReminder,
      );
      setDueDateReminderChecked(
        getReminderSettingsQuery.data.apiData.isDueDateReminder,
      );
    }
  }, [getReminderSettingsQuery.data, reset]);

  const onSubmit = handleSubmit((data) => {
    if (isSubmitting) return;
    data.libraryID = libraryID || "";

    updateMutation.mutate(data, {
      onSettled(response) {
        handleResponse(response);
      },
    });
  });

  const handleResponse = async (response: ApiResponse<number> | undefined) => {
    if (response?.status == 200) {
      if (editMode) {
        queryClient.invalidateQueries({
          queryKey: ["library", "getEmailSettings", "EmailSettings"],
        });
      }
      showSuccess(`Record ${editMode ? "updated" : "added"} successfully`);
      navigateTo(`../edit/${libraryID}/reminder`);
    } else {
      if (response?.status == 404) {
        showError(response?.title ?? "");
      } else if (response?.status == 400) {
        if (response?.modelErrors) {
          showServerErrors(setError, response.errors);
          showError(response?.title ?? "");
        } else {
          showError(response?.title ?? "");
        }
      } else {
        showError(response?.title ?? "");
      }
    }
  };

  return (
    <MainCard
      content={false}
      darkTitle
      darktitleVariant={"h4"}
      title={editMode ? "Reminder Settings" : "Add Library"}
    >
      <Box component={"form"} sx={{ p: 2.5 }} onSubmit={onSubmit}>
        {getReminderSettingsQuery.isFetching ? <Loader /> : <></>}
        <Grid container spacing={2} direction={"column"}>
          <Grid container item xs={12} md={6} lg={6} alignItems="center">
            <Grid item xs={10} sm={8} md={8} lg={6}>
              <InputLabel htmlFor="isOverdueReminder">
                <Stack direction={"row"} alignItems={"center"} spacing={1}>
                  <Stack>Do you want to send mail for Overdue Reminder?</Stack>
                  <Stack>
                    <Tooltip
                      title="A mail will send to member whose due days Overdue (If Enabled)"
                      arrow
                    >
                      <InfoCircleFilled />
                    </Tooltip>
                  </Stack>
                </Stack>
              </InputLabel>
            </Grid>
            <Grid item xs={2} sm={4} md={4} lg={2}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    {...register("isOverDueReminder")}
                    checked={overDueReminderChecked}
                    onChange={handleOverDueReminderChange}
                    id="isOverdueReminder"
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                label=""
              />
            </Grid>
          </Grid>
          <Divider sx={{ py: 1 }} />
          <Grid container item xs={12} md={6} lg={6} alignItems="center">
            <Grid item xs={10} sm={8} md={8} lg={6}>
              <InputLabel htmlFor="isDueDateReminder">
                <Stack direction={"row"} alignItems={"center"} spacing={1}>
                  <Stack>Do you want to send email before Due Date ?</Stack>
                  <Stack>
                    <Tooltip
                      title="A mail will send to member before the due date (If Enabled)"
                      arrow
                    >
                      <InfoCircleFilled />
                    </Tooltip>
                  </Stack>
                </Stack>
              </InputLabel>
            </Grid>
            <Grid item xs={2} sm={4} md={4} lg={2}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    {...register("isDueDateReminder")}
                    checked={dueDateReminderChecked}
                    onChange={handleDueDateReminderChange}
                    id="isDueDateReminder"
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                label=""
              />
            </Grid>
          </Grid>

          <Divider sx={{ py: 1 }} />
          <Grid container item xs={12} md={6} lg={6} alignItems="center">
            <Grid item xs={10} sm={8} md={8} lg={6}>
              <InputLabel htmlFor="dueDateReminderDays">
                <Stack direction={"row"} alignItems={"center"} spacing={1}>
                  <Stack>
                    Number of days before the due date to send a reminder email.
                  </Stack>
                  <Stack>
                    <Tooltip
                      title="Number of days before the due date to send a reminder email."
                      arrow
                    >
                      <InfoCircleFilled />
                    </Tooltip>
                  </Stack>
                </Stack>
              </InputLabel>
            </Grid>
            <Grid item xs={2} sm={4} md={4} lg={3}>
              <TextField
                autoComplete="off"
                fullWidth
                disabled={!dueDateReminderChecked}
                InputProps={{
                  onKeyDown: (e) => {
                    const allowedKeys = [
                      "Backspace",
                      "Tab",
                      "ArrowLeft",
                      "ArrowRight",
                      "Delete",
                    ];

                    if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                      e.preventDefault();
                    }
                  },
                  onWheel: (e) => {
                    (e.target as HTMLElement).blur();
                  },
                }}
                id="dueDateReminderDays"
                {...register("dueDateReminderDays")}
                error={!!errors.dueDateReminderDays}
                helperText={
                  errors.dueDateReminderDays
                    ? errors.dueDateReminderDays?.message
                    : ""
                }
                placeholder="Enter Due Date Reminder Days"
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid container item spacing={2} mb={0} pt={2}>
          <Grid item sm={12}>
            <Button
              title={editMode ? "Update" : "Save"}
              type="submit"
              disabled={isSubmitting}
              variant="contained"
            >
              {isSubmitting && (
                <CircularProgress color="inherit" size="1rem" sx={{ mr: 1 }} />
              )}
              {editMode ? "Update" : "Save"}
            </Button>
            <Button
              title="Back"
              sx={{ ml: 1 }}
              variant="contained"
              color="secondary"
              disabled={isSubmitting}
              onClick={() => navigateTo(`/admin/library`)}
            >
              Back
            </Button>
          </Grid>
        </Grid>
      </Box>
    </MainCard>
  );
};
export default ReminderSettingsTab;
